<template>
  <atlas-header
    :is-front-page="showFrontpageHeader"
    :show-hot-topic="showHotTopic"
    :skip-link="skipLinkData"
    @interaction="handleDataLayerInteraction"
  >
    <template #header-menu-items>
      <dn-link
        v-if="alarmIcon && rtConfPub.alerts"
        v-track-event="{ eventName: 'Header', component: { zone: 'header', element: 'dn-alert', element_id: 'menu-alert' }, attributes: { link_url: 'dn.no/alerts' } }"
        class="button-icon-only button-alert"
        icon="alarm-light"
        href="/alerts"
        external
      />
      <client-only>
        <dn-ma-frame
          v-if="shouldRenderBannerCard(headerCTA) && showHeaderCTA && !isPreviewMode()"
          :id="headerCTA.id"
          class="dn-ma-frame-headercta"
        />
      </client-only>
      <client-only v-if="rtConfPub.auth">
        <dn-subscribe-button
          v-if="!rtConfPub.hideSubscribeButton"
          v-track-event="{ eventName: 'Header', component: { zone: 'header', element: 'dn-subscribe', element_id: 'menu-subscribe' }, attributes: { link_url: 'dn.no/subscribe' } }"
        />
        <dn-login-button @show-user-menu="userMenuClicked" />
      </client-only>
    </template>
  </atlas-header>

  <div class="menu-wrapper-user">
    <dn-user-menu
      v-if="showUserMenu && rtConfPub.auth"
      :payload="loginPayload"
      @hide-user-menu="hideUserMenu"
    />
  </div>
</template>

<script setup lang="ts">
import { AtlasHeader } from '@nhst/ui';
import { useRoute } from 'vue-router';
import { useProfileLoginStore } from '~/store/profileLogin';

const rtConfPub = useRuntimeConfig().public;
const { t } = useI18n();
const route = useRoute();
const routeName = computed(() => {
  const name = getRouteName(route.name?.toString());
  if (name === 'latest' || name === 'search') {
    return 'section';
  } else {
    return name;
  }
});
const isFrontpage = computed(() => routeName.value === 'frontpage');

const showFrontpageHeader = computed(() => isFrontpage.value && rtConfPub?.showFrontpageHeader);

const showHotTopic = computed(() => isFrontpage.value && rtConfPub?.showHotTopic);
const skipLinkData = {
  text: t('Skip to main content'),
  ariaLabel: t('Skip to main content'),
  target: ['.dn-teaser-card', 'h1[tabindex="-1"]', '.dn-card_assets', '.dn-teaser-old .dn-link']
};
const alarmIcon = rtConfPub?.config?.header?.alarmIcon;
const showUserMenu = ref(false);
const loginPayload = ref({});
const showHeaderCTA = useProfileLogin(useProfileLoginStore().loginStatus).shouldShowHeaderCTA;
const headerCTA = computed(() => getBrazeConfig().bannerConfig?.[routeName.value]?.static?.headerCTA);

const scrollTop = () => {
  const topScrollAd = document.querySelectorAll('.adnm-topscroll:not(.adnm-topscroll-fixed)') || '';
  if (topScrollAd.length > 0) {
    window.scrollTo(0, 0);
    document.querySelector('html')?.classList.add('adnm-topscroll-hide');
  }
};
const scrollNormal = () => {
  const topScrollAdFixed = document.querySelectorAll('.adnm-topscroll-fixed') || '';
  const topHeader = document.querySelectorAll('.header') || '';
  if (topScrollAdFixed.length > 0) {
    if (topHeader.length > 0) {
      window.scrollTo(0, 1);
    }
    document.querySelector('html')?.classList.remove('adnm-topscroll-hide');
  } else {
    document.querySelector('html')?.classList.remove('adnm-topscroll-hide');
  }
};

const userMenuClicked = (payload: any) => {
  showUserMenu.value = true;
  loginPayload.value = payload;
  document.body.classList.add('user-menu-open', 'no-scroll');
  scrollTop();
  const overlay = document.querySelector('.overlay') as HTMLElement | null;
  if (overlay) {
    overlay.click();
  }
};

const hideUserMenu = () => {
  showUserMenu.value = false;
  document.body.classList.remove('user-menu-open', 'no-scroll');
  scrollNormal();
};

const handleDataLayerInteraction = (interaction: any) => {
  useDataLayerEvent().handleDataLayerInteractionEvent(interaction, interaction.event?.target);
  // New tracking library — runs alongside the old system during transition.
  useTrackingEvent().handleInteractionEvent(interaction, interaction.event?.target);
};
</script>

<style lang="scss">
.atlas-header {
  .dn-link {
    &.button-icon-only {
      --color-icon: var(--color-header-top-fg);

      .dn-icon {
        width: 24px;
        max-width: 24px;
      }
    }
  }
}

.adnm-html-topscroll-frame-wrapper {
  .adnm-topscroll-hide & {
    height: 0 !important;
    opacity: 0;
    z-index: 1 !important;
  }
}
</style>
